import React, { useState, useEffect } from 'react';
import { AlertTriangle, MessageSquare, CheckCircle, Eye, Clock, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal, LabeledInput } from '../../components/SharedUI';
import { getTmComplaints, createTmComplaint, respondToTmComplaint, deleteTmComplaint } from '../../utils/tm_api';

export default function TmComplaintsPage() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = ['owner', 'admin'].includes(user.role);

  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setComplaints(await getTmComplaints());
    } catch (e) { toast.error('خطأ'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const statusBadge = (s) => {
    const map = {
      open: { label: 'مفتوحة', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
      in_review: { label: 'قيد المراجعة', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' },
      resolved: { label: 'محلولة', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
      dismissed: { label: 'مرفوضة', cls: 'bg-gray-200 text-gray-500 dark:bg-gray-800 dark:text-gray-400' }
    };
    const info = map[s] || map.open;
    return <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${info.cls}`}>{info.label}</span>;
  };

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--text-dark-h)]">الشكاوى</h1>
            <p className="text-sm text-[var(--text-muted)]">{complaints.length} شكوى</p>
          </div>
        </div>
        {!isOwner && (
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all font-bold text-sm">
            <AlertTriangle className="w-4 h-4" /> رفع شكوى
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[var(--text-muted)]">جاري التحميل...</div>
      ) : complaints.length === 0 ? (
        <div className="text-center py-12 text-[var(--text-muted)]">لا توجد شكاوى</div>
      ) : (
        <div className="space-y-3">
          {complaints.map(c => (
            <div key={c.id} className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 hover:shadow-md transition-all cursor-pointer"
              onClick={() => setShowDetail(c)}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {statusBadge(c.status)}
                    <span className="text-xs text-[var(--text-muted)]">#{c.id}</span>
                  </div>
                  <h3 className="font-bold text-sm text-[var(--text-dark-h)]">{c.subject}</h3>
                  <p className="text-xs text-[var(--text-gray)] mt-1 line-clamp-2">{c.description}</p>
                </div>
                <div className="text-left shrink-0">
                  <p className="text-xs text-[var(--text-muted)]">{new Date(c.created_at).toLocaleDateString('ar-IQ')}</p>
                  <p className="text-xs text-[var(--text-gray)]">{c.complainant_name}</p>
                </div>
              </div>
              {c.task_title && (
                <p className="mt-2 text-xs bg-[var(--bg-flat-50)] p-1.5 rounded-lg text-[var(--text-gray)]">
                  المهمة: {c.task_title}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && <CreateComplaintModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}

      {/* Detail Modal */}
      {showDetail && <ComplaintDetailModal complaint={showDetail} isOwner={isOwner}
        onClose={() => setShowDetail(null)} onUpdated={() => { setShowDetail(null); load(); }} />}
    </div>
  );
}

function CreateComplaintModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ subject: '', description: '', against_type: 'general', task_id: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.subject || !form.description) return toast.error('الموضوع والوصف مطلوبان');
    setSaving(true);
    try {
      await createTmComplaint(form);
      toast.success('تم إرسال الشكوى');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="رفع شكوى" onClose={onClose}>
      <div className="space-y-4">
        <LabeledInput label="الموضوع *">
          <input type="text" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm" />
        </LabeledInput>
        <LabeledInput label="الوصف *">
          <textarea rows="4" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm" />
        </LabeledInput>
        <div>
          <label className="block text-xs font-bold text-[var(--text-gray)] mb-1">النوع</label>
          <select value={form.against_type} onChange={e => setForm(f => ({ ...f, against_type: e.target.value }))}
            className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm">
            <option value="general">عام</option>
            <option value="team_leader">ضد قائد فريق</option>
            <option value="supervisor">ضد متابع</option>
            <option value="task_related">متعلقة بمهمة</option>
          </select>
        </div>
        <button onClick={handleSubmit} disabled={saving}
          className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-all disabled:opacity-50">
          {saving ? 'جاري الإرسال...' : 'إرسال الشكوى'}
        </button>
      </div>
    </Modal>
  );
}

function ComplaintDetailModal({ complaint, isOwner, onClose, onUpdated }) {
  const [response, setResponse] = useState(complaint.owner_response || '');
  const [status, setStatus] = useState(complaint.status);
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleRespond = async () => {
    setSaving(true);
    try {
      await respondToTmComplaint(complaint.id, { owner_response: response, status });
      toast.success('تم التحديث');
      onUpdated();
    } catch (e) { toast.error('خطأ'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteTmComplaint(complaint.id);
      toast.success('تم حذف الشكوى');
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ في الحذف'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`شكوى: ${complaint.subject}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="p-3 rounded-xl bg-[var(--bg-flat-50)]">
          <p className="text-xs text-[var(--text-muted)] mb-1">من: {complaint.complainant_name} — {new Date(complaint.created_at).toLocaleDateString('ar-IQ')}</p>
          <p className="text-sm text-[var(--text-dark-h)]">{complaint.description}</p>
        </div>

        {complaint.owner_response && (
          <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-900/30">
            <p className="text-xs text-green-700 dark:text-green-400 font-bold mb-1"><MessageSquare className="w-3.5 h-3.5 inline ml-1" /> رد المالك:</p>
            <p className="text-sm text-[var(--text-dark-h)]">{complaint.owner_response}</p>
          </div>
        )}

        {isOwner && (
          <div className="space-y-3 pt-3 border-t border-[var(--border-light)]">
            <LabeledInput label="الرد">
              <textarea rows="4" value={response} onChange={e => setResponse(e.target.value)} className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm" />
            </LabeledInput>
            <div>
              <label className="block text-xs font-bold text-[var(--text-gray)] mb-1">الحالة</label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm">
                <option value="open">مفتوحة</option>
                <option value="in_review">قيد المراجعة</option>
                <option value="resolved">محلولة</option>
                <option value="dismissed">مرفوضة</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={handleRespond} disabled={saving}
                className="flex-1 py-3 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold transition-all disabled:opacity-50">
                {saving ? 'جاري الحفظ...' : 'حفظ الرد'}
              </button>
              {confirmDelete ? (
                <div className="flex gap-1 bg-red-50 dark:bg-red-900/20 px-2 rounded-xl items-center">
                  <span className="text-xs text-red-600 font-bold whitespace-nowrap">متأكد؟</span>
                  <button onClick={handleDelete} className="p-2 text-red-600 hover:text-red-700"><CheckCircle className="w-4 h-4" /></button>
                  <button onClick={() => setConfirmDelete(false)} className="p-2 text-gray-500 hover:text-gray-700"><XCircle className="w-4 h-4" /></button>
                </div>
              ) : (
                <button onClick={() => setConfirmDelete(true)} disabled={saving}
                  className="px-4 py-3 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40 rounded-xl font-bold transition-all disabled:opacity-50" title="حذف الشكوى">
                  <Trash2 className="w-5 h-5 mx-auto" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
